import React, { useState, useEffect } from 'react';
import {
  getAuth,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  Timestamp
} from 'firebase/firestore';

import {
  Line,
  Doughnut
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CentralAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // We store both the current "live" stats + growth rates in one object
  const [stats, setStats] = useState({
    current: {
      totalAuthUsers: 0,
      totalFirestoreUsers: 0,
      proUsers: 0,
      freeUsers: 0,
      paidInvoices: 0,
      totalInvoices: 0,
      totalAgreements: 0,
      newUsersWeek: 0,
      newUsersMonth: 0,
      newUsersQuarter: 0,
      // funnel steps
      funnel: {
        signedUp: 0,
        proConverted: 0,
        invoicesCreated: 0,
        invoicesPaid: 0
      },
      // user breakdown array
      userBreakdown: []
    },
    // Growth compares to last snapshot
    growth: {
      totalAuthUsers: 0,
      proUsers: 0,
      paidInvoices: 0,
      funnelProConversion: 0
      // etc...
    }
  });

  const [timeRange, setTimeRange] = useState('week');
  const [lastSnapshot, setLastSnapshot] = useState(null); // optional: store old snapshot

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (
        user &&
        (user.email === 'elivnatwiine@gmail.com' || user.uid === 'Gv8azk4pYrgKq9nnYS4dpezw1Ej2')
      ) {
        setIsAuthorized(true);
        await refreshData();
      } else {
        setIsAuthorized(false);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Main refresh function
  const refreshData = async () => {
    setLoading(true);
    try {
      // 1) Fetch live stats from Firestore
      const currentStats = await fetchFirestoreAnalytics();
      // 2) If you also fetch total Auth user count via a function, do so here:
      const totalAuthUsers = await fetchAuthUserCount();
      currentStats.totalAuthUsers = totalAuthUsers;

      // 3) Compare + store
      const { updatedStats, oldSnapshot } = await compareAndStoreAnalytics(currentStats);

      // 4) Put final stats in state
      setStats(updatedStats);
      setLastSnapshot(oldSnapshot);
    } catch (e) {
      console.error('Error refreshing data:', e);
    }
    setLoading(false);
  };

  // Example getAuthUserCount
  const fetchAuthUserCount = async () => {
    try {
      const res = await fetch('https://<YOUR_PROJECT>.cloudfunctions.net/getAuthUserCount');
      const data = await res.json();
      return data.totalAuthUsers || 0;
    } catch (error) {
      console.error('Error fetching Auth user count:', error);
      return 0;
    }
  };

  /**
   * Gathers "live" stats from Firestore,
   * including user breakdown details.
   */
  const fetchFirestoreAnalytics = async () => {
    const db = getFirestore();

    const now = Timestamp.now();
    const weekAgo = Timestamp.fromMillis(now.toMillis() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = Timestamp.fromMillis(now.toMillis() - 30 * 24 * 60 * 60 * 1000);
    const quarterAgo = Timestamp.fromMillis(now.toMillis() - 90 * 24 * 60 * 60 * 1000);

    // --- 1) USERS
    const usersSnap = await getDocs(collection(db, 'users'));
    const totalFirestoreUsers = usersSnap.size;
    const userDocs = usersSnap.docs.map(doc => ({
      userId: doc.id,
      ...doc.data()
    }));

    const proUsers = userDocs.filter(u => u.isPro).length;
    const freeUsers = totalFirestoreUsers - proUsers;

    // New user counts
    const newUsersWeek = userDocs.filter(u => {
      const createdTs = u.createdAt?.toMillis?.() || 0;
      return createdTs >= weekAgo.toMillis();
    }).length;

    const newUsersMonth = userDocs.filter(u => {
      const createdTs = u.createdAt?.toMillis?.() || 0;
      return createdTs >= monthAgo.toMillis();
    }).length;

    const newUsersQuarter = userDocs.filter(u => {
      const createdTs = u.createdAt?.toMillis?.() || 0;
      return createdTs >= quarterAgo.toMillis();
    }).length;

    // --- 2) INVOICES
    const invoicesSnap = await getDocs(collection(db, 'invoices'));
    const invoiceDocs = invoicesSnap.docs.map(doc => doc.data());
    const totalInvoices = invoiceDocs.length;
    const paidInvoices = invoiceDocs.filter(inv => inv.status === 'paid').length;

    // --- 3) AGREEMENTS
    const agreementsSnap = await getDocs(collection(db, 'agreements'));
    const agreementDocs = agreementsSnap.docs.map(doc => doc.data());
    const totalAgreements = agreementDocs.length;

    // --- 4) Build funnel
    const funnel = {
      signedUp: totalFirestoreUsers,
      proConverted: proUsers,
      invoicesCreated: totalInvoices,
      invoicesPaid: paidInvoices
    };

    // --- 5) Build user breakdown
    // We want { userId, username, email, isPro, invoiceCount, agreementCount }
    const userMap = {};
    userDocs.forEach(u => {
      userMap[u.userId] = {
        userId: u.userId,
        username: u.username ||
          u.name ||
          (u.email ? u.email.split('@')[0] : 'Unknown'),
        email: u.email || 'N/A',
        isPro: !!u.isPro,
        invoiceCount: 0,
        agreementCount: 0
      };
    });

    // Tally invoices by user
    invoiceDocs.forEach(inv => {
      if (inv.userId && userMap[inv.userId]) {
        userMap[inv.userId].invoiceCount++;
      }
    });

    // Tally agreements by user
    agreementDocs.forEach(agr => {
      if (agr.userId && userMap[agr.userId]) {
        userMap[agr.userId].agreementCount++;
      }
    });

    // Convert map to array + sort by pro first, then total docs
    const userBreakdown = Object.values(userMap);
    userBreakdown.sort((a, b) => {
      if (a.isPro !== b.isPro) return a.isPro ? -1 : 1; // pro first
      const docsA = a.invoiceCount + a.agreementCount;
      const docsB = b.invoiceCount + b.agreementCount;
      return docsB - docsA;
    });

    // Return final
    return {
      totalFirestoreUsers,
      proUsers,
      freeUsers,
      paidInvoices,
      totalInvoices,
      totalAgreements,
      newUsersWeek,
      newUsersMonth,
      newUsersQuarter,
      funnel,
      userBreakdown
    };
  };

  /**
   * Compare the new stats vs. the last saved snapshot in /analytics/latest,
   * compute growth percentages, store the new snapshot,
   * and return { updatedStats, oldSnapshot }.
   */
  const compareAndStoreAnalytics = async (currentStats) => {
    const db = getFirestore();
    const latestRef = doc(db, 'analytics', 'latest');
    const latestSnap = await getDoc(latestRef);

    const now = new Date();

    let oldSnapshot = null;
    let growth = {
      totalAuthUsers: 0,
      proUsers: 0,
      paidInvoices: 0,
      funnelProConversion: 0
    };

    if (latestSnap.exists()) {
      oldSnapshot = latestSnap.data();
      // Calculate growth for each key
      growth.totalAuthUsers = computeGrowthPct(
        oldSnapshot.totalAuthUsers || 0,
        currentStats.totalAuthUsers || 0
      );
      growth.proUsers = computeGrowthPct(
        oldSnapshot.proUsers || 0,
        currentStats.proUsers || 0
      );
      growth.paidInvoices = computeGrowthPct(
        oldSnapshot.paidInvoices || 0,
        currentStats.paidInvoices || 0
      );
      // For funnel pro conversions (old => new)
      growth.funnelProConversion = computeGrowthPct(
        oldSnapshot.proUsers || 0,
        currentStats.proUsers || 0
      );
    }

    // Write the new snapshot to Firestore
    const newSnapshot = {
      timestamp: now,
      totalAuthUsers: currentStats.totalAuthUsers || 0,
      totalFirestoreUsers: currentStats.totalFirestoreUsers || 0,
      proUsers: currentStats.proUsers || 0,
      paidInvoices: currentStats.paidInvoices || 0,
      totalInvoices: currentStats.totalInvoices || 0,
      totalAgreements: currentStats.totalAgreements || 0
      // You can store more if you like
    };

    await setDoc(latestRef, newSnapshot);

    // Merge into final shape for UI
    const updatedStats = {
      current: {
        ...currentStats
      },
      growth
    };

    return { updatedStats, oldSnapshot };
  };

  /**
   * Helper to compute percentage growth from oldVal => newVal
   * e.g. oldVal=100, newVal=107 => 7%
   */
  const computeGrowthPct = (oldVal, newVal) => {
    if (oldVal <= 0) {
      return newVal > 0 ? 100 : 0; // If old was 0, handle edge case
    }
    const diff = newVal - oldVal;
    return ((diff / oldVal) * 100).toFixed(1);
  };

  const handleTimeRangeChange = async (range) => {
    setTimeRange(range);
    // If you want to do something with time-range-specific stats, do it here
  };

  // UI RENDERING
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="text-center bg-white dark:bg-black p-8 rounded-xl shadow-md">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-500 dark:text-gray-400">You are not authorized to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Destructure
  const { current, growth } = stats;
  const { userBreakdown } = current;

  // funnel conversion
  const funnelConversion = current.funnel.invoicesCreated
    ? ((current.funnel.invoicesPaid / current.funnel.invoicesCreated) * 100).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-black shadow-sm dark:border-b dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Klorah Analytics</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Real-time insights into how Klorah is growing
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={refreshData}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-black border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <span className="dark:text-gray-200">Refresh</span>
            </button>
            <select
              className="form-select rounded-lg border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-200 bg-white dark:bg-black"
              onChange={e => handleTimeRangeChange(e.target.value)}
              value={timeRange}
            >
              <option value="week">Last 7 days</option>
              <option value="month">Last 30 days</option>
              <option value="quarter">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* TOP STAT CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <ModernStatCard
            title="Auth Users"
            value={current.totalAuthUsers}
            growth={growth.totalAuthUsers}
            subtitle="Current users that can log in"
          />
          <ModernStatCard
            title="Firestore Users"
            value={current.totalFirestoreUsers}
            growth={0}
            subtitle={`Pro: ${current.proUsers} | Free: ${current.freeUsers}`}
          />
          <ModernStatCard
            title="Pro Subscribers"
            value={current.proUsers}
            growth={growth.proUsers}
            subtitle="All-time Pro"
          />
          <ModernStatCard
            title="Paid Invoices"
            value={current.paidInvoices}
            growth={growth.paidInvoices}
            subtitle="All-time"
          />
        </div>

        {/* FUNNEL */}
        <div className="bg-white dark:bg-black rounded-xl shadow-sm dark:border dark:border-gray-800 p-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Conversion Funnel</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Track user progress from signup to Pro to invoice creation to paid
          </p>
          <div className="flex flex-col md:flex-row items-center justify-around gap-8">
            <FunnelStep
              label="Signed Up"
              value={current.funnel.signedUp}
            />
            <FunnelStep
              label="Pro Conversions"
              value={current.funnel.proConverted}
              growth={growth.funnelProConversion}
            />
            <FunnelStep
              label="Invoices Created"
              value={current.funnel.invoicesCreated}
            />
            <FunnelStep
              label="Invoices Paid"
              value={current.funnel.invoicesPaid}
            />
          </div>
          <div className="mt-4 text-center">
            <span className="inline-block text-sm text-gray-700 dark:text-gray-300 font-medium">
              {funnelConversion}% of created invoices are paid
            </span>
          </div>
        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-black rounded-xl shadow-sm dark:border dark:border-gray-800 p-6">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">User Growth</h3>
            <Line
              data={{
                labels: ['90d', '30d', '7d', 'Now'],
                datasets: [
                  {
                    label: 'New Users',
                    data: [
                      current.newUsersQuarter,
                      current.newUsersMonth,
                      current.newUsersWeek,
                      current.totalFirestoreUsers
                    ],
                    borderColor: '#4F46E5',
                    tension: 0.4,
                    fill: false
                  }
                ]
              }}
              options={{ 
                responsive: true,
                plugins: {
                  legend: {
                    labels: {
                      color: 'rgb(156, 163, 175)' // text-gray-400
                    }
                  }
                },
                scales: {
                  x: {
                    grid: {
                      color: 'rgba(75, 85, 99, 0.2)'
                    },
                    ticks: {
                      color: 'rgb(156, 163, 175)'
                    }
                  },
                  y: {
                    grid: {
                      color: 'rgba(75, 85, 99, 0.2)'
                    },
                    ticks: {
                      color: 'rgb(156, 163, 175)'
                    }
                  }
                }
              }}
            />
          </div>

          <div className="bg-white dark:bg-black rounded-xl shadow-sm dark:border dark:border-gray-800 p-6">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">Invoice Status</h3>
            <Doughnut
              data={{
                labels: ['Paid', 'Pending+Draft'],
                datasets: [
                  {
                    data: [
                      current.paidInvoices,
                      current.totalInvoices - current.paidInvoices // approximate
                    ],
                    backgroundColor: ['#22C55E', '#EAB308']
                  }
                ]
              }}
              options={{
                plugins: {
                  legend: {
                    labels: {
                      color: 'rgb(156, 163, 175)'
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* ADDITIONAL STATS CARDS */}
        <div className="bg-white dark:bg-black rounded-xl shadow-sm dark:border dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-2">Additional Stats</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Total agreements, new users by timeframe, etc.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            <ModernStatCard
              title="Agreements"
              value={current.totalAgreements}
              subtitle="All-time"
            />
            <ModernStatCard
              title="New Users (7d)"
              value={current.newUsersWeek}
              subtitle=""
            />
            <ModernStatCard
              title="New Users (30d)"
              value={current.newUsersMonth}
              subtitle=""
            />
            <ModernStatCard
              title="New Users (90d)"
              value={current.newUsersQuarter}
              subtitle=""
            />
          </div>
        </div>

        {/* USER BREAKDOWN TABLE */}
        <div className="bg-white dark:bg-black rounded-xl shadow-sm dark:border dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">User Breakdown</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Sorted by Pro status and total document activity (invoices + agreements).
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-gray-800 dark:text-gray-100">
              <thead className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="py-3 px-2 text-left">User</th>
                  <th className="py-3 px-2 text-left">Status</th>
                  <th className="py-3 px-2 text-left">Invoices</th>
                  <th className="py-3 px-2 text-left">Agreements</th>
                  <th className="py-3 px-2 text-left">Total Docs</th>
                </tr>
              </thead>
              <tbody>
                {userBreakdown.map((u) => {
                  const totalDocs = u.invoiceCount + u.agreementCount;
                  return (
                    <tr
                      key={u.userId}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                    >
                      <td className="py-4 px-2">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full overflow-hidden flex-shrink-0">
                            <img
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(u.username)}&background=random`}
                              alt=""
                            />
                          </div>
                          <div className="ml-3">
                            <div className="font-medium">{u.username}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            u.isPro
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                          }`}
                        >
                          {u.isPro ? 'Pro' : 'Basic'}
                        </span>
                      </td>
                      <td className="py-4 px-2">{u.invoiceCount}</td>
                      <td className="py-4 px-2">{u.agreementCount}</td>
                      <td className="py-4 px-2">{totalDocs}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

/** 
 * A modern Airbnb-esque stat card with growth percentage
 */
const ModernStatCard = ({
  title,
  value,
  growth = 0,
  subtitle = ''
}) => {
  const isPositive = parseFloat(growth) > 0;
  const isZero = parseFloat(growth) === 0;

  return (
    <div className="rounded-xl bg-white dark:bg-black p-6 shadow hover:shadow-md transition-shadow space-y-2 dark:border dark:border-gray-800">
      <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
        {title}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          {value}
        </span>
        {!isZero && (
          <span
            className={`text-sm font-semibold flex items-center gap-1 ${
              isPositive
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {isPositive
              ? <ArrowTrendingUpIcon className="w-4 h-4" />
              : <ArrowTrendingDownIcon className="w-4 h-4" />}
            {growth}%
          </span>
        )}
      </div>
      {subtitle && (
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {subtitle}
        </div>
      )}
    </div>
  );
};

/** 
 * A funnel step component 
 */
const FunnelStep = ({ label, value, growth }) => {
  const isPositive = growth && parseFloat(growth) > 0;

  return (
    <div className="text-center">
      <div className="text-3xl font-bold text-gray-800 dark:text-gray-100 flex items-center justify-center gap-1">
        {value}
        {growth && (
          <span
            className={`text-sm font-semibold flex items-center gap-1 ${
              isPositive
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {isPositive
              ? <ArrowTrendingUpIcon className="w-4 h-4" />
              : <ArrowTrendingDownIcon className="w-4 h-4" />}
            {growth}%
          </span>
        )}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {label}
      </div>
    </div>
  );
};

export default CentralAnalytics;